import { useMemo } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Switch, Tooltip } from '@mui/material';
import { getNumericStringSortComparator } from '../../utils/sortComparators';
import { PortfolioDataTable } from '../../types/generatedTypes';

interface UseCustodianColumnsProps {
  autosizeColumns: boolean;
  isGroupingEnabled?: boolean;
  onToggleUploadTransactions?: (portfolioId: string, enabled: boolean) => void;
}

export const useCustodianColumns = ({
  autosizeColumns,
  isGroupingEnabled = false,
  onToggleUploadTransactions,
}: UseCustodianColumnsProps) => {
  const columns: GridColDef[] = useMemo(
    () => [
      // Dedicated group column - only shown when grouping is enabled
      ...(isGroupingEnabled
        ? [
            {
              field: 'group_name',
              headerName: 'Group',
              ...(autosizeColumns ? {} : { flex: 1.2 }),
              sortable: false,
              filterable: false,
              pinnable: false, // Prevent user from unpinning
              hideable: false, // Prevent user from hiding
              valueGetter: (value: any, row: any) => {
                // For group rows, show the group name
                if (row.isGroup) {
                  return row.groupName;
                }
                // For regular rows, return empty string
                return '';
              },
            },
          ]
        : []),
      {
        field: 'upload_transactions_enabled',
        headerName: 'Upload Transactions',
        width: 160,
        editable: false,
        sortable: true,
        renderCell: (params: GridRenderCellParams<PortfolioDataTable>) => {
          if (params.row.isGroup) {
            return null;
          }
          const portfolioId = params.row.id;
          if (!portfolioId) {
            return null;
          }
          return (
            <Tooltip title="When disabled, transactions for this custodian will not be uploaded">
              <Switch
                checked={params.value !== false}
                onChange={e =>
                  onToggleUploadTransactions?.(portfolioId, e.target.checked)
                }
                size="small"
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'client_name',
        headerName: 'Client Name',
        ...(autosizeColumns ? {} : { flex: 1.2 }),
        editable: false,
        sortable: true,
      },
      {
        field: 'account_number',
        headerName: 'Account Number',
        ...(autosizeColumns ? {} : { flex: 1 }),
        editable: true,
        sortable: true,
        getSortComparator: getNumericStringSortComparator,
      },
      {
        field: 'portfolio_name',
        headerName: 'Portfolio Name',
        ...(autosizeColumns ? {} : { flex: 1.2 }),
        editable: false,
        sortable: true,
      },
      {
        field: 'portfolio_id',
        headerName: 'Portfolio ID',
        ...(autosizeColumns ? {} : { flex: 1 }),
        editable: false,
        sortable: true,
        getSortComparator: getNumericStringSortComparator,
      },
      {
        field: 'custodian_name',
        headerName: 'Custodian Name',
        ...(autosizeColumns ? {} : { flex: 1 }),
        editable: false,
        sortable: true,
      },
      {
        field: 'custodian_id',
        headerName: 'Custodian ID',
        ...(autosizeColumns ? {} : { flex: 1 }),
        editable: false,
        sortable: true,
        getSortComparator: getNumericStringSortComparator,
      },
      {
        field: 'leading_currency',
        headerName: 'Leading Currency',
        ...(autosizeColumns ? { minWidth: 150 } : { flex: 1 }),
        editable: false,
        sortable: true,
      },
    ],
    [autosizeColumns, isGroupingEnabled, onToggleUploadTransactions]
  );

  return {
    columns,
  };
};
